import { signalingRequest } from "@/lib/signaling";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ComponentType,
	type Ref,
	type ReactNode,
} from "react";
import {
	Check,
	Copy,
	Download,
	Keyboard,
	Mouse,
	MousePointerClick,
	Move,
	Terminal,
	Type,
	CircleDot,
	CircleSlash,
} from "lucide-react";
import { DeviceInspector } from "./DeviceInspector";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select.tsx";
import { cn } from "@/lib/utils";
import { XtermLogPanel } from "@/components/XtermLogPanel.tsx";
import { NetworkRequestInspector } from "@/components/session/NetworkRequestInspector.tsx";
import { buildNetworkHar } from "@/lib/networkInspection";
import { useLiveNetworkInspection, useTunnelStatus } from "@/hooks/useNetworkInspection.ts";
import type { BuildLogLine } from "@/client/director/buildLogs.tsx";
import type { SessionEvent, SessionLogLine } from "@/client/director/sessionArtifacts.tsx";

// useLiveSessionStream opens an SSE connection to one of the runtime's
// session endpoints ({apiUrl}/session/applogs/events or /session/events/events)
// and accumulates the streamed JSON entries. Replay-then-tail on the runtime
// side means a capture already running when the page opens is fully covered.
// Entries accumulate in place and flush at most once per animation frame so a
// chatty app does not re-render the page once per log line. A transient drop
// is left to EventSource's built-in auto-reconnect; the runtime resumes
// after the Last-Event-ID the browser sends, so a reconnect delivers only
// entries not yet received and the accumulated list never needs a reset.
//
// The returned clear function hides everything received so far without
// touching the connection: the stream is per-instance, not per-capture, so
// when the user switches the watched app the old app's lines would otherwise
// stay mixed into the view. Clearing is a baseline index rather than a
// reconnect because a fresh EventSource would replay the runtime's buffer
// and bring the old lines right back.
export function useLiveSessionStream<T extends { ts: number }>(
	url: string | undefined,
): [T[], () => void] {
	const [entries, setEntries] = useState<T[]>([]);
	const entriesRef = useRef<T[]>([]);
	const baselineRef = useRef(0);
	const frameRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		entriesRef.current = [];
		baselineRef.current = 0;
		setEntries([]);
		if (!url) return;
		const es = new EventSource(url);
		const flush = () => {
			frameRef.current = undefined;
			setEntries(entriesRef.current.slice(baselineRef.current));
		};
		es.onmessage = (e) => {
			try {
				const parsed = JSON.parse(e.data) as T;
				if (typeof parsed.ts !== "number") return;
				entriesRef.current.push(parsed);
				if (frameRef.current === undefined) {
					frameRef.current = requestAnimationFrame(flush);
				}
			} catch {
				// Skip malformed frame.
			}
		};
		return () => {
			es.close();
			if (frameRef.current !== undefined) {
				cancelAnimationFrame(frameRef.current);
				// Reset so the re-run's onmessage can schedule flushes again;
				// a stale id here would block them forever.
				frameRef.current = undefined;
			}
		};
	}, [url]);

	const clear = useCallback(() => {
		baselineRef.current = entriesRef.current.length;
		setEntries([]);
	}, []);

	return [entries, clear];
}

function formatRelativeTime(tsMs: number): string {
	const diff = Math.floor((Date.now() - tsMs) / 1000);
	if (diff < 5) return "now";
	if (diff < 60) return `${diff}s ago`;
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	return `${Math.floor(diff / 3600)}h ago`;
}

// eventVisual maps an event type to its timeline icon and label; unknown
// types (the contract may grow) fall back to a neutral dot.
const eventVisuals: Record<string, { icon: ComponentType<{ className?: string }>; label: string }> =
	{
		tap: { icon: MousePointerClick, label: "Tap" },
		drag: { icon: Move, label: "Drag" },
		scroll: { icon: Mouse, label: "Scroll" },
		key: { icon: Keyboard, label: "Key" },
		text: { icon: Type, label: "Text" },
		command: { icon: Terminal, label: "Command" },
	};

function eventVisual(type: string) {
	return eventVisuals[type] ?? { icon: CircleDot, label: type };
}

// eventDetail derives the one-line description shown next to the type label:
// the runtime's summary when present, else coordinates/counts per type.
function eventDetail(event: SessionEvent): string {
	if (event.summary) return event.summary;
	const parts: string[] = [];
	if (event.x != null && event.y != null) {
		parts.push(`(${Math.round(event.x)}, ${Math.round(event.y)})`);
	}
	if (event.toX != null && event.toY != null) {
		parts.push(`→ (${Math.round(event.toX)}, ${Math.round(event.toY)})`);
	}
	if (event.count != null) parts.push(`×${event.count}`);
	if (event.durationMs != null) parts.push(`${event.durationMs}ms`);
	return parts.join(" ");
}

// EventRow is one entry of an event timeline, shared by the live panel and
// the playback-synced panel. Rows are buttons when a seek callback is given;
// active marks the row playback is currently at.
export function EventRow({
	event,
	timeLabel,
	onClick,
	active,
	rowRef,
}: {
	event: SessionEvent;
	timeLabel: string;
	onClick?: () => void;
	active?: boolean;
	rowRef?: Ref<HTMLButtonElement>;
}) {
	const { icon: Icon, label } = eventVisual(event.type);
	const body = (
		<>
			<Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
			<div className="flex min-w-0 flex-1 flex-col">
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-xs font-medium">{label}</span>
					<span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
						{timeLabel}
					</span>
				</div>
				{eventDetail(event) && (
					<span className="truncate text-xs text-muted-foreground" title={eventDetail(event)}>
						{eventDetail(event)}
					</span>
				)}
			</div>
		</>
	);
	if (onClick) {
		return (
			<button
				ref={rowRef}
				type="button"
				onClick={onClick}
				className={cn(
					"flex w-full items-start gap-2 rounded border-l-2 px-2 py-1 text-left transition-colors",
					active
						? "border-l-blue-600 bg-slate-200/80 hover:bg-slate-200"
						: "border-l-transparent hover:bg-muted/50",
				)}
			>
				{body}
			</button>
		);
	}
	return <div className="flex items-start gap-2 px-2 py-1">{body}</div>;
}

// LiveEventTimeline renders the streamed events newest-last and keeps the
// view pinned to the bottom while new events arrive.
function LiveEventTimeline({ events }: { events: SessionEvent[] }) {
	const bottomRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: "end" });
	}, [events.length]);
	return (
		<ScrollArea className="min-h-0 flex-1">
			{events.length === 0 ? (
				<p className="p-3 text-sm text-muted-foreground">
					No events yet. Interact with the instance to see them here.
				</p>
			) : (
				<div className="flex flex-col gap-0.5 p-2">
					{events.map((event, i) => (
						<EventRow
							key={`${event.ts}-${i}`}
							event={event}
							timeLabel={formatRelativeTime(event.ts)}
						/>
					))}
					<div ref={bottomRef} />
				</div>
			)}
		</ScrollArea>
	);
}

// icon is a base64 PNG both runtimes inline per app, best-effort; when a
// runtime cannot extract one the picker falls back to a letter avatar.
// installedAt is epoch milliseconds of install, absent when the runtime
// cannot tell (e.g. iOS system apps).
type InstalledApp = {
	bundleId: string;
	name: string;
	installType: string;
	icon?: string;
	installedAt?: number;
};

function AppIcon({ app }: { app: InstalledApp }) {
	if (app.icon) {
		return (
			<img
				src={`data:image/png;base64,${app.icon}`}
				alt=""
				className="h-4 w-4 shrink-0 rounded-sm"
			/>
		);
	}
	return (
		<span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-muted text-[9px] font-semibold uppercase text-muted-foreground">
			{app.name.charAt(0)}
		</span>
	);
}

// listInstalledApps normalizes the two runtimes' listAppsResult shapes:
// limulator returns apps as a JSON string at the top level, scrcpy-webrtc
// nests the array under payload. Entries carry the same fields either way.
async function listInstalledApps(wsUrl: string): Promise<InstalledApp[]> {
	const msg = await signalingRequest(wsUrl, "listApps", {});
	const raw = typeof msg.apps === "string" ? JSON.parse(msg.apps) : msg.payload?.apps;
	const apps = (Array.isArray(raw) ? raw : []) as InstalledApp[];
	return apps.filter((a) => a.bundleId && a.name).sort((a, b) => a.name.localeCompare(b.name));
}

// AppLogSourcePicker lets the user pick an installed app to tail: selecting
// one stops whatever app-log capture is running, starts a capture for the
// chosen app (persisted when session recording is on, live-only otherwise),
// and launches the app. The launch is what makes logs flow on iOS: limulator
// tails an app's output only when it launched the app itself, so watching
// without launching shows nothing for apps opened by tapping the home screen.
function AppLogSourcePicker({
	signalingUrl,
	platform,
	persist,
	onCaptureStarted,
	onWatched,
}: {
	signalingUrl: string;
	platform?: "ios" | "android";
	// Start new captures with persistence, set while session recording is on.
	persist?: boolean;
	// Fired once the new capture is running, so the panel can drop the
	// previous app's lines from the shared per-instance stream.
	onCaptureStarted?: () => void;
	// Reports which app is being watched, so session recording can restart
	// its capture with persistence.
	onWatched?: (bundleId: string) => void;
}) {
	const [apps, setApps] = useState<InstalledApp[] | "loading" | undefined>(undefined);
	const [selected, setSelected] = useState<string | undefined>(undefined);
	const [error, setError] = useState<string | undefined>(undefined);

	const loadApps = () => {
		if (apps !== undefined) return;
		setApps("loading");
		listInstalledApps(signalingUrl)
			.then(setApps)
			.catch((e: Error) => {
				setApps(undefined);
				setError(e.message);
			});
	};

	// Guards against overlapping picks: the runtime has a single app-log
	// capture, so interleaved stop/start pairs would leave it and the
	// dropdown out of sync. A pick that lands mid-switch is not dropped
	// but stashed (latest wins) and run when the in-flight switch ends;
	// with the launch included a switch takes long enough for a second
	// click to land inside it.
	const switching = useRef(false);
	const pendingPick = useRef<string | undefined>(undefined);
	const watchApp = async (bundleId: string) => {
		if (switching.current) {
			pendingPick.current = bundleId;
			return;
		}
		switching.current = true;
		const previous = selected;
		setSelected(bundleId);
		setError(undefined);
		try {
			// Only one app-log capture runs at a time; stop the current one
			// first (a no-op error when nothing runs is ignored).
			await signalingRequest(signalingUrl, "stopAppLogCapture", {}, 5_000).catch(() => {});
			// Capture before launch so the app's startup lines are caught.
			await signalingRequest(signalingUrl, "startAppLogCapture", {
				bundleId,
				...(persist ? { persist: true } : {}),
			});
			onCaptureStarted?.();
			onWatched?.(bundleId);
			// Launch through the runtime: iOS carries the app as bundleId,
			// Android as packageName; each ignores the other's field. On
			// iOS the launch is relaunch-if-running because limulator can
			// only attach its log tailer to a process it spawns — merely
			// foregrounding a running app would keep its logs invisible.
			// Android's logcat needs no such restart. A launch failure
			// keeps the capture (its logs may still flow) but is surfaced.
			await signalingRequest(signalingUrl, "launchApp", {
				bundleId,
				packageName: bundleId,
				...(platform === "ios" ? { mode: "RelaunchIfRunning" } : {}),
			}).catch((e: Error) => {
				setError(`launch failed: ${e.message}`);
			});
		} catch (e) {
			// Roll the dropdown back so the failed app stays selectable
			// (Radix does not re-fire onValueChange for the shown value).
			setSelected(previous);
			setError(e instanceof Error ? e.message : "failed to start capture");
		} finally {
			switching.current = false;
		}
		const next = pendingPick.current;
		pendingPick.current = undefined;
		if (next !== undefined && next !== bundleId) void watchApp(next);
	};

	return (
		<div className="ml-auto flex min-w-0 items-center gap-2">
			{error && (
				<span className="truncate text-[10px] text-red-600" title={error}>
					{error}
				</span>
			)}
			<Select value={selected} onValueChange={watchApp} onOpenChange={(open) => open && loadApps()}>
				<SelectTrigger className="h-8 w-44 px-2 text-xs">
					<SelectValue placeholder="Watch an app..." />
				</SelectTrigger>
				<SelectContent>
					{apps === "loading" || apps === undefined ? (
						<div className="px-2 py-1.5 text-xs text-muted-foreground">Loading apps...</div>
					) : (
						apps.map((app) => (
							<SelectItem key={app.bundleId} value={app.bundleId} className="text-xs">
								<span className="flex items-center gap-1.5">
									<AppIcon app={app} />
									{app.name}
									<span className="font-mono text-[10px] text-muted-foreground/50">
										{app.bundleId}
									</span>
								</span>
							</SelectItem>
						))
					)}
				</SelectContent>
			</Select>
		</div>
	);
}

function LiveBadge() {
	return (
		<Badge
			variant="outline"
			className="border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
		>
			live
		</Badge>
	);
}

// LiveCapturePanels streams the runtime's session events and app logs over
// SSE and renders them as the left-hand column of a streaming view: events
// on top, app logs on the bottom. apiUrl is the instance's router API URL;
// the instance token authenticates the SSE requests as a query parameter
// (EventSource cannot set headers). signalingUrl, when given, enables the
// app picker that starts app-log captures from the console.
export function LiveCapturePanels({
	apiUrl,
	token,
	signalingUrl,
	platform,
	className,
	embedded = false,
	network,
	stats,
	defaultTab,
	onStatsActiveChange,
}: {
	apiUrl: string;
	token?: string;
	signalingUrl?: string;
	// Which runtime the picker talks to; iOS needs a different launch mode.
	platform?: "ios" | "android";
	className?: string;
	embedded?: boolean;
	network?: ReactNode;
	stats?: ReactNode;
	defaultTab?: "console" | "stats";
	onStatsActiveChange?: (active: boolean) => void;
}) {
	const liveUrl = (path: string) =>
		`${apiUrl}${path}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
	const signalingUrlWithToken = signalingUrl
		? `${signalingUrl}${signalingUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token ?? "")}`
		: undefined;
	// Events should always flow while the panel is open, so start a live-only
	// event capture unconditionally: when the instance was created with
	// --events a persisting capture already runs and the runtime answers
	// "already active", which is exactly the state we want, so all errors
	// are ignored.
	useEffect(() => {
		if (!signalingUrlWithToken) return;
		signalingRequest(signalingUrlWithToken, "startEventCapture", { persist: false }).catch(
			() => {},
		);
	}, [signalingUrlWithToken]);
	const [liveEvents, clearEvents] = useLiveSessionStream<SessionEvent>(
		liveUrl("/session/events/events"),
	);
	const [liveAppLogs, clearAppLogs] = useLiveSessionStream<SessionLogLine>(
		liveUrl("/session/applogs/events"),
	);
	// The xterm panel consumes the build-log line shape; each app log line
	// becomes one stdout entry (perLine adds the newline).
	const liveAppLogLines = useMemo(
		(): BuildLogLine[] => liveAppLogs.map((l) => ({ type: "stdout", data: l.line })),
		[liveAppLogs],
	);

	// Session recording is one-way for the instance's lifetime: everything
	// from the click on is persisted, and the runtimes upload in-flight
	// captures on termination, so no stop control is needed. The video
	// recording starts fresh; the running live-only event and app-log
	// captures are restarted with persistence.
	const [recording, setRecording] = useState<"off" | "starting" | "on">("off");
	const [recordError, setRecordError] = useState<string | undefined>(undefined);
	const watchedApp = useRef<string | undefined>(undefined);
	const startSessionRecording = async () => {
		if (!signalingUrlWithToken || recording !== "off") return;
		setRecording("starting");
		setRecordError(undefined);
		const failures: string[] = [];
		const attempt = async (type: string, payload: Record<string, unknown>) => {
			await signalingRequest(signalingUrlWithToken, type, payload).catch((e: Error) => {
				failures.push(`${type}: ${e.message}`);
			});
		};
		// The two runtimes name the video command differently.
		await attempt(platform === "ios" ? "startVideoRecording" : "startRecording", {
			persist: true,
		});
		await signalingRequest(signalingUrlWithToken, "stopEventCapture", {}, 5_000).catch(() => {});
		await attempt("startEventCapture", { persist: true });
		if (watchedApp.current) {
			await signalingRequest(signalingUrlWithToken, "stopAppLogCapture", {}, 5_000).catch(() => {});
			await attempt("startAppLogCapture", {
				bundleId: watchedApp.current,
				persist: true,
			});
		}
		if (failures.length > 0) setRecordError(failures[0]);
		// Partial persistence (e.g. video already recording) still counts as
		// on; only a complete failure leaves the button clickable again.
		setRecording(failures.length >= 2 ? "off" : "on");
	};

	const recordingControl = signalingUrlWithToken && (
		<div className="flex min-w-0 items-center gap-2">
			<button
				type="button"
				onClick={startSessionRecording}
				disabled={recording !== "off"}
				title="Persist this session's video, events and app logs; watch them on the Session page after the instance ends."
				className={cn(
					"flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
					recording === "on"
						? "border-red-200 bg-red-50 text-red-700"
						: "border-input bg-background text-foreground hover:bg-muted",
					embedded && "h-8 rounded-md border-transparent px-2 font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
				)}
			>
				<span
					className={cn(
						"h-2 w-2 rounded-full",
						recording === "on" ? "animate-pulse bg-red-600" : "bg-red-500",
					)}
				/>
				{recording === "on"
					? "Recording session"
					: recording === "starting"
						? "Starting..."
						: "Record session"}
			</button>
			{recordError && (
				<span className="truncate text-[10px] text-red-600" title={recordError}>
					{recordError}
				</span>
			)}
		</div>
	);
	const appPicker = signalingUrlWithToken && (
		<AppLogSourcePicker
			signalingUrl={signalingUrlWithToken}
			platform={platform}
			persist={recording === "on"}
			onCaptureStarted={clearAppLogs}
			onWatched={(bundleId) => {
				watchedApp.current = bundleId;
			}}
		/>
	);

	if (embedded) {
		return <DeviceInspector className={className} defaultTab={defaultTab}
			onStatsActiveChange={onStatsActiveChange} network={network} stats={stats} footer={recordingControl}
			console={<>
				<div className="flex h-11 shrink-0 items-center gap-2 border-b px-3">
					<Button
						variant="ghost"
						size="icon"
						className="size-7 shrink-0"
						aria-label="Clear console"
						title="Clear console"
						onClick={clearAppLogs}
					>
						<CircleSlash className="size-3.5" />
					</Button>
					<span className="text-xs text-muted-foreground">App output</span>
					{appPicker}
				</div>
				<XtermLogPanel lines={liveAppLogLines} perLine />
				<div className="flex h-7 shrink-0 items-center border-t px-3 text-xs text-muted-foreground">
					{liveAppLogs.length} messages
				</div>
			</>}
			events={<>
				<div className="flex h-11 shrink-0 items-center gap-2 border-b px-3">
					<Button
						variant="ghost"
						size="icon"
						className="size-7 shrink-0"
						aria-label="Clear events"
						title="Clear events"
						onClick={clearEvents}
					>
						<CircleSlash className="size-3.5" />
					</Button>
					<span className="text-xs text-muted-foreground">Device interactions</span>
				</div>
				<LiveEventTimeline events={liveEvents} />
				<div className="flex h-7 shrink-0 items-center border-t px-3 text-xs text-muted-foreground">
					{liveEvents.length} events
				</div>
			</>}
		/>;
	}

	return (
		<div className={cn("flex min-h-0 flex-col gap-3", className)}>
			{recordingControl}
			<div
				className={cn(
					"flex min-h-0 flex-1 flex-col overflow-hidden bg-background",
					!embedded && "rounded-lg border",
				)}
			>
				<div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
					<h2 className="text-sm font-semibold">Live events</h2>
					<LiveBadge />
				</div>
				<LiveEventTimeline events={liveEvents} />
			</div>
			<div
				className={cn(
					"flex min-h-0 flex-1 flex-col overflow-hidden bg-background",
					!embedded && "rounded-lg border",
				)}
			>
				<div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
					<h2 className="shrink-0 text-sm font-semibold">Live app logs</h2>
					<LiveBadge />
					{appPicker}
				</div>
				<XtermLogPanel lines={liveAppLogLines} perLine />
			</div>
		</div>
	);
}

// UnavailablePanel keeps a panel's place and title when the credential in hand
// cannot open it, such as a stream-only link, instead of mounting the live
// panel only to show its 403.
export function UnavailablePanel({
	title,
	message,
	className,
}: {
	title: string;
	message: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-background",
				className,
			)}
		>
			<div className="flex items-center gap-2 border-b px-3 py-2">
				<h2 className="text-sm font-semibold">{title}</h2>
			</div>
			<p className="p-3 text-sm text-muted-foreground">{message}</p>
		</div>
	);
}

// tunnelApiUrlFor returns the HTTP base of an instance's tunnel REST
// endpoints (/tunnel/status, the inspection SSE). iOS serves them on the
// instance apiUrl; Android serves them from the websocket-proxy daemon behind
// the adbWebSocket route.
export function tunnelApiUrlFor(
	platform: "ios" | "android",
	status: { apiUrl?: string; adbWebSocketUrl?: string },
): string | undefined {
	if (platform === "ios") return status.apiUrl;
	const adbWebSocketUrl = status.adbWebSocketUrl;
	if (!adbWebSocketUrl) return undefined;
	try {
		const url = new URL(adbWebSocketUrl);
		url.protocol = url.protocol === "ws:" ? "http:" : "https:";
		return url.toString();
	} catch {
		return undefined;
	}
}

// LiveNetworkPanel is the right-hand column of a streaming view: the live
// network requests flowing through the instance's tunnel. When no tunnel is
// running it shows the CLI command that starts one, so the feature is
// discoverable right where its output would appear.
export function LiveNetworkPanel({
	tunnelApiUrl,
	token,
	platform,
	instanceId,
	className,
	embedded = false,
}: {
	// HTTP base of the instance's tunnel endpoints; undefined when the
	// instance does not expose one.
	tunnelApiUrl?: string;
	token?: string;
	platform?: "ios" | "android";
	// Shown in the CLI hint so the command targets this instance as-is.
	instanceId?: string;
	className?: string;
	embedded?: boolean;
}) {
	const { status: tunnelStatus, error: tunnelStatusError } = useTunnelStatus(
		tunnelApiUrl,
		token,
		Boolean(tunnelApiUrl),
	);
	const activeTunnel = tunnelStatus === "loading" ? undefined : tunnelStatus.active;
	const liveNetwork = useLiveNetworkInspection(
		tunnelApiUrl,
		token,
		activeTunnel?.tunnelId,
		activeTunnel?.state === "ready" && activeTunnel.inspection.enabled,
	);
	return (
		<div
			className={cn(
				"flex min-h-0 flex-col overflow-hidden bg-background",
				!embedded && "rounded-lg border",
				className,
			)}
		>
			<div
				className={cn(
					"flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2",
					embedded && "min-h-11 px-3 py-2",
				)}
			>
				{!embedded && <h2 className="text-sm font-semibold">Network requests</h2>}
				{embedded ? (
					<span className="text-xs text-muted-foreground">
						{activeTunnel
							? liveNetwork.connection === "reconnecting"
								? "Reconnecting"
								: activeTunnel.state
							: "No tunnel connected"}
					</span>
				) : activeTunnel ? (
					<Badge
						variant="outline"
						className="border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
					>
						{liveNetwork.connection === "reconnecting" ? "reconnecting" : activeTunnel.state}
					</Badge>
				) : (
					<Badge variant="outline" className="px-2 py-0.5 text-xs font-medium">
						no tunnel
					</Badge>
				)}
				{activeTunnel?.selectors.map((selector) => (
					<Badge key={selector.id} variant="outline" className="font-mono text-[10px]">
						{selector.value}
					</Badge>
				))}
				{liveNetwork.requests.length > 0 && (
					<button
						type="button"
						onClick={() => {
							const blob = new Blob([buildNetworkHar(liveNetwork.requests)], {
								type: "application/json",
							});
							const url = URL.createObjectURL(blob);
							const anchor = document.createElement("a");
							anchor.href = url;
							anchor.download = `network-${instanceId ?? "session"}.har`;
							anchor.click();
							URL.revokeObjectURL(url);
						}}
						title="Download the captured requests as a HAR file"
						className="ml-auto rounded-md border border-input bg-background p-1 text-foreground hover:bg-muted"
					>
						<Download className="h-3.5 w-3.5" />
					</button>
				)}
			</div>
			<div
				className={cn(
					"min-h-0 flex-1 overflow-auto",
					(!embedded || !activeTunnel?.inspection.enabled) && "p-3",
				)}
			>
				{tunnelStatusError ? (
					<p className="text-sm text-red-600">
						Failed to load tunnel status: {tunnelStatusError.message}
					</p>
				) : activeTunnel ? (
					activeTunnel.inspection.enabled ? (
						<NetworkRequestInspector
							requests={liveNetwork.requests}
							warnings={liveNetwork.warnings}
							variant="compact"
							embedded={embedded}
							emptyMessage={
								liveNetwork.connection === "connecting"
									? "Connecting to the live inspection stream..."
									: "No inspected requests yet."
							}
						/>
					) : (
						<p className="text-sm text-muted-foreground">
							Network inspection is disabled for this tunnel.
						</p>
					)
				) : (
					<TunnelCommandHint instanceId={instanceId} platform={platform} />
				)}
			</div>
			{embedded && (
				<div className="flex h-7 shrink-0 items-center border-t px-3 text-xs text-muted-foreground">
					{liveNetwork.requests.length} requests
				</div>
			)}
		</div>
	);
}

// TunnelCommandHint shows the CLI command that starts a tunnel for this
// instance, with a copy button so it can be pasted into a terminal as-is.
function TunnelCommandHint({
	instanceId,
	platform,
}: {
	instanceId?: string;
	platform?: "ios" | "android";
}) {
	const command = `lim ${platform === "ios" ? "ios" : "android"} tunnel --selector localhost:8080 --persist${instanceId ? ` --id ${instanceId}` : ""}`;
	const [copied, setCopied] = useState(false);
	return (
		<div className="space-y-2 text-sm text-muted-foreground">
			<p>
				No tunnel is running. Start one locally to route the instance's traffic through your machine
				and inspect it here:
			</p>
			<div className="flex items-start gap-2">
				<pre className="min-w-0 flex-1 overflow-x-auto rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground">
					{command}
				</pre>
				<button
					type="button"
					onClick={() => {
						navigator.clipboard.writeText(command).then(() => {
							setCopied(true);
							setTimeout(() => setCopied(false), 1_500);
						});
					}}
					title="Copy the tunnel command"
					className="rounded-md border border-input bg-background p-1.5 text-foreground hover:bg-muted"
				>
					{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
				</button>
			</div>
		</div>
	);
}
